<template>
    <MktRichContent :content="contentChunks" />
</template>

<script lang="ts" setup>
    import type {RichContentChunk} from '@nuxt-marketing-module/shared/types/RichContent';
    import type {RTNode} from '@prismicio/types';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {ArticleContentSlice} from '@prismicio-types';
    import {MktRichContent} from '#components';

    const props = defineProps({
        ...getSliceComponentProps<ArticleContentSlice>(),
    });

    function isTitleRTNode(rtNode: RTNode) {
        return rtNode.type === 'heading2' || rtNode.type === 'heading3';
    }

    const contentChunks = (props.slice.primary.content as RTNode[]).reduce<RichContentChunk[]>((acc: RichContentChunk[], current: RTNode) => {
        if (isTitleRTNode(current)) {
            acc.push({type: 'title', content: [current]});
        } else {
            const lastChunk = acc[acc.length - 1];
            if (!lastChunk || lastChunk.type === 'title') {
                acc.push({type: isTitleRTNode(current) ? 'title' : 'content', content: [current]});
            } else {
                acc[acc.length - 1].content.push(current as never);
            }
        }
        return acc;
    }, []);
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .article-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        @include content-width($article-max-width);
        p,
        ol,
        ul,
        li {
            font-size: 18px;
        }
        h1,
        h2 {
            margin-bottom: var(--joy-core-spacing-8);
            font-size: var(--joy-font-size-secondary-200);
        }
        h3,
        h4,
        h5,
        h6,
        p,
        ul,
        ol {
            margin-bottom: var(--joy-core-spacing-7);
        }
        h3 {
            font-size: var(--joy-font-size-primary-800);
        }
        .block-img {
            display: flex;
            justify-content: center;
        }
        img {
            margin-bottom: var(--joy-core-spacing-14);
            max-width: 100%;
        }
        a {
            color: var(--joy-color-primary-50);
            text-decoration: underline var(--joy-color-primary-50);
            &:hover {
                color: var(--joy-color-primary-70);
                text-decoration: underline var(--joy-color-primary-70);
            }
        }
        div {
            width: 100%;
        }
        div[data-oembed-type='video'] {
            width: 100%;
            padding-top: 56.25%; // 16:9
            position: relative;
            iframe {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                width: 100%;
                height: 100%;
            }
        }
        .title-anchor {
            position: relative;
            top: -92px;
            visibility: hidden;
            @media (max-width: $mobile-breakpoint) {
                top: -76px;
            }
        }

        &--alignTitlesCenter {
            h1,
            h2 {
                text-align: center;
            }
        }
    }
</style>
